import React from "react";
import { ChangeEvent } from "react";
import { RouteComponentPropsHistory } from "../../types";
import { Link } from "react-router-dom";

import { DateTime } from "luxon";

import { apiGet, V2 } from "../../Api";
import { formatDateLocalDayOfWeek, formatName, formatTimeLocal, formatList } from "../../Utilities";
import { IAuthProps, WellKnownGroups } from "../AuthContext";
import HasRight from "../HasRight";
import ViewToolbar from "../ViewToolbar";
import withTitle, { IViewTitleProps } from "../withTitle";
import withAuth from "../withAuth";

import "../../arrow.css";
import "../../blocks.css";
import "../../button.css";
import "../../schedule.css";
import "../viewer.css";

interface IHealthFairDayScheduleViewState {
    date: DateTime;
    events: { [time: string]: any[] };
    isLoading: boolean | string;
    isSelectionMode: boolean;

    searchPerson: string;

    people: any[];

    vacationPeople: any[];
    notices: any[];
    onCallNotices: any[];

    [name: string]: any;
}

interface IHealthFairDayScheduleViewProps extends RouteComponentPropsHistory<any, any>, IViewTitleProps, IAuthProps {

}

class HealthFairDayScheduleView extends React.Component<IHealthFairDayScheduleViewProps, IHealthFairDayScheduleViewState> {
    private refreshTimer: number | undefined;

    constructor(props: Readonly<IHealthFairDayScheduleViewProps>) {
        super(props);

        const newDate = DateTime.fromISO(this.props.match.params.date);

        this.state = {
            date: newDate,
            events: {},
            isLoading: true,
            isSelectionMode: (props.location.state && props.location.state.selectionMode) || false,

            searchPerson: this.props.location.state
                ? this.props.location.state.searchPerson
                : (this.props.authContext.isInGroup(WellKnownGroups.HfTeam) ? "caller" : ""),

            people: [],

            vacationPeople: [],
            notices: [],
            onCallNotices: []
        }

        this.prevDay = this.prevDay.bind(this);
        this.nextDay = this.nextDay.bind(this);
        this.handleElementChange = this.handleElementChange.bind(this);
        this.handleSelectionClick = this.handleSelectionClick.bind(this);
    }

    public async componentDidMount() {
        try {
            const marketers = true/*this.props.authContext.hasRight("appt:readall")*/
                ? await apiGet<any[]>(`employees/summary?group=${WellKnownGroups.HfTeam}`)
                : [];

            this.setState({
                people: marketers.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name)))
            });

            this.requestEvents();
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }

        this.refreshTimer = window.setInterval(() => this.requestEvents(), 10 * 60 * 1000);
    }

    public componentWillUnmount() {
        if (this.refreshTimer) {
            window.clearInterval(this.refreshTimer);
            this.refreshTimer = undefined;
        }
    }

    public render() {
        let content;

        if (typeof this.state.isLoading === "string") {
            content = <div className="error-text">{this.state.isLoading}</div>;
        }
        else if (this.state.isLoading) {
            content = <div className="lds-dual-ring spinner-center" />;
        }
        else if (Object.keys(this.state.events).length === 0) {
            content = <span>No health fairs found</span>;
        }
        else {
            content = Object.keys(this.state.events).map(k => (
                <section key={k} className="schedule-section">
                    <h3>{formatTimeLocal(this.state.date.set({ hour: Math.floor(parseInt(k, 10) / 60), minute: Math.floor(parseInt(k, 10) % 60) }))}</h3>
                    <ul>
                        {
                            this.state.events[k].map((e: any) => (
                                <li key={e.id}>
                                    {this.state.isSelectionMode
                                        ? <div className="selection-outer">
                                            <Link to={`/fairs/${e.id}`} className="link">
                                                <span>{e.name}</span>
                                                <span className="small-text">{e.estimatedHeadCount} &mdash; {`${e.address.city}, ${e.address.state}`}</span>
                                                <span className="small-text member-list">{e.members.map((m: any) => formatName(m.name)).join(", ")}</span>
                                            </Link>
                                            <button type="button" onClick={() => this.handleSelectionClick(e.id, e.name)} title="Select fair">
                                                <i className="icon-check" />
                                            </button>
                                        </div>
                                        : <Link to={`/fairs/${e.id}`}>
                                            <span>{e.name}</span>
                                            <span className="small-text">{e.estimatedHeadCount} &mdash; {`${e.address.city}, ${e.address.state}`}</span>
                                            <span className="small-text member-list">{e.members.map((m: any) => formatName(m.name)).join(", ")}</span>
                                        </Link>}
                                </li>
                            ))
                        }
                    </ul>
                </section>
            ));
        }

        return (
            <div className="viewer-root">
                <ViewToolbar>
                    {!this.state.isSelectionMode
                        ? <HasRight right="fair:create">
                            {!this.props.authContext.serverType ? <Link to={{ pathname: "/fairtargets", state: { date: this.state.date.toISODate() } }} className="icon-plus" /> : null}
                        </HasRight>
                        : null}
                </ViewToolbar>
                {this.state.isSelectionMode
                    ? <div className="status-box slim">
                        Click the check mark on a health fair below to select it.
                    </div>
                    : null}
                <div className="filter-control-box">
                    <div className="control-group">
                        <button className="button btn-light arrow arrow-left arrow-box" onClick={this.prevDay} />
                        <button className="button btn-light arrow arrow-right arrow-box" onClick={this.nextDay} />
                        <span>{formatDateLocalDayOfWeek(this.state.date)}</span>
                    </div>
                    <select className="push-right-if-wide" value={this.state.searchPerson} name="searchPerson" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">Everyone</option>
                        <option value="caller">You</option>
                        {
                            this.state.people.map(a =>
                                <option key={a.id} value={a.id}>{formatName(a.name)}</option>
                            )
                        }
                    </select>
                </div>
                {this.state.vacationPeople.length > 0
                    ? <div className="status-box slim">
                        {formatList(this.state.vacationPeople, (item, i) => i % 2 === 0 ? <b key={item.id}>{item.name}</b> : item)} {this.state.vacationPeople.length === 1 ? "is" : "are"} off this day.
                    </div>
                    : null}
                {this.state.notices.length > 0
                    ? this.state.notices.map(a => <div key={a.id} className="orange-box slim">{a.text}</div>)
                    : null}
                {this.state.onCallNotices.length > 0
                    ? this.state.onCallNotices.map(a => <div key={a.id} className="orange-box slim">{a.text}</div>)
                    : null}
                <div className="info-block">
                    <header>
                        <h3>{this.state.isLoading ? "Loading..." : this.personToString(this.state.searchPerson)}</h3>
                    </header>
                    {content}
                </div>
            </div>
        );
    }

    private async requestEvents() {
        try {
            const start = this.state.date.startOf("day");
            const end = start.plus({ day: 1 });

            const parameters = [
                `date_min=${start.toSeconds().toFixed(0)}`,
                `date_max=${end.toSeconds().toFixed(0)}`
            ];
            const vacationParameters = [
                `after=${start.toISODate()}`,
                `before=${start.toISODate()}`,
                `group=${WellKnownGroups.HfTeam}`
            ];
            const noticeParameters = [
                `after=${start.toISODate()}`,
                `before=${start.toISODate()}`,
            ];

            if (this.state.searchPerson) {
                parameters.push(`person=${this.state.searchPerson}`);
                if (this.state.searchPerson !== "caller") {
                    vacationParameters.push(`employee=${this.state.searchPerson}`);
                }
            }

            const [json, vacation, notices, onCallStatusTelemarketer, onCallStatusSurgicalConsult] = await Promise.all([
                apiGet<any>(`fairs?${parameters.join("&")}`),
                V2.get<any>(`schedule/vacation?${vacationParameters.join("&")}`),
                V2.get<any[]>(`fairs/notices?${noticeParameters.join("&")}`),
                V2.get<any[]>(`schedule/coverage/status/${WellKnownGroups.PatientCareRep}/${start.toISODate()}`),
                V2.get<any[]>(`schedule/coverage/status/${WellKnownGroups.SurgicalConsultant}/${start.toISODate()}`)
            ]);

            const today = DateTime.local();
            if (start.hasSame(today, "day")) {
                this.props.viewTitleContext.changeTitle("Today's Health Fairs");
            }
            else {
                this.props.viewTitleContext.changeTitle("Health Fairs");
            }
            this.props.viewTitleContext.changeTitle(`Health Fairs - ${formatDateLocalDayOfWeek(this.state.date)}`, "window");


            const map: { [time: string]: any } = {};
            for (const event of json) {
                event.scheduledDate = DateTime.fromISO(event.scheduledDate).toLocal();
                const timeBin = (event.scheduledDate.hour * 60) + event.scheduledDate.minute;
                if (!map[timeBin]) {
                    map[timeBin] = [];
                }
                map[timeBin].push(event);
            }

            // Notify if no one is on call
            let onCallNotices = [];
            if (onCallStatusTelemarketer.length === 0) {
                onCallNotices.push("patient care rep.");
            }
            if (onCallStatusSurgicalConsult.length === 0) {
                onCallNotices.push("surgical consultant");
            }

            if (onCallNotices.length > 0) {
                onCallNotices = [{
                    id: "no-on-call",
                    text: `No ${formatList(onCallNotices, undefined, "or")} ${onCallNotices.length === 1 ? "is" : "are"} on call this day`
                }];
            }

            this.setState({
                isLoading: false,
                events: map,
                vacationPeople: vacation.map((a: any) => ({
                    id: a.employee.id,
                    name: formatName(a.employee.name)
                })),
                notices,
                onCallNotices
            });
        }
        catch (e) {
            // Request failed
            this.setState({
                isLoading: "Failed to load schedule"
            })
        }
    }

    private prevDay() {
        // Yesterday
        this.gotoDate(this.state.date.minus({ day: 1 }));
    }

    private nextDay() {
        // Tomorrow
        this.gotoDate(this.state.date.plus({ day: 1 }));
    }

    private gotoDate(date: DateTime) {
        this.setState(s => {
            const currentLocation = this.props.location;
            currentLocation.pathname = `/fairs/schedule/${date.toISODate()}`;
            this.props.history.replace(currentLocation);

            return {
                date
            }
        }, () => this.requestEvents());
    }

    private saveSearchState() {
        // Stash the currently displayed month so the user can come back to it
        const currentLocation = this.props.location;
        currentLocation.state = {
            searchPerson: this.state.searchPerson,
            selectionMode: this.state.isSelectionMode,
        };
        this.props.history.replace(currentLocation);
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            [name]: value
        }, () => { this.saveSearchState(); this.requestEvents(); });
    }

    private personToString(person: string) {
        switch (person) {
            case "": return "Everyone";
            case "caller": return "You";
            default:
                const per = this.state.people.find(a => a.id === person);
                return per ? formatName(per.name) : person;
        }
    }

    private handleSelectionClick(id: string, name: string) {
        const { selectionUrl, selectionState } = this.props.location.state;
        if (!selectionUrl) {
            return;
        }

        selectionState.selectedFairId = id;
        selectionState.selectedFairName = name;

        this.props.history.replace(selectionUrl, selectionState);
    }
}

export default withTitle("Health Fairs", withAuth(HealthFairDayScheduleView));
